"use client"

import { useMemo, useState } from "react"
import {
    Clock,
    Handshake,
    Laptop,
    type LucideIcon,
    MonitorPlay,
    Search,
    Users,
    Wrench,
} from "lucide-react"

import { Input } from "@/common/components/ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/common/components/ui/select"
import { cn } from "@/common/lib/utils"
import { BAUSTEINE } from "@/modules/lernpfad/data/bausteine"
import { SKILL_BY_ID } from "@/modules/lernpfad/data/skills"
import type { Baustein, BausteinFormat, SkillCategory } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"

/**
 * The course catalogue, browsed from the outside — as an employee at the
 * customer's own LMS would see it, not as Compass's curated recommendation.
 * Every Baustein is listed here regardless of whether it is on anyone's plan;
 * the plan is Compass's judgement about WHICH of these matter to whom, and
 * that judgement is deliberately absent from this screen.
 */

const FORMAT_ICON: Record<BausteinFormat, LucideIcon> = {
    "e-learning": Laptop,
    praesenz: Users,
    webinar: MonitorPlay,
    selbststudium: Clock,
    "on-the-job": Wrench,
    social: Handshake,
}

/**
 * Per-format accent, generic Tailwind palette rather than the app's own
 * `--primary` tokens — this catalogue has to read as a foreign LMS's own UI,
 * not a Compass screen, so it never touches the brand accent (see the file
 * header). Colour by format gives the grid visual variety and lets a learner
 * recognise a modality at a glance instead of reading six words of muted text.
 */
const FORMAT_COLOR: Record<BausteinFormat, string> = {
    "e-learning": "bg-sky-500/10 text-sky-700",
    webinar: "bg-violet-500/10 text-violet-700",
    praesenz: "bg-amber-500/10 text-amber-700",
    selbststudium: "bg-slate-500/10 text-slate-700",
    "on-the-job": "bg-emerald-500/10 text-emerald-700",
    social: "bg-rose-500/10 text-rose-700",
}

const FORMATS: BausteinFormat[] = [
    "e-learning",
    "webinar",
    "praesenz",
    "selbststudium",
    "on-the-job",
    "social",
]
const CATEGORIES: SkillCategory[] = ["regulatorik", "fach", "fuehrung", "digital", "methodik"]

/** A Baustein's primary category — the category of the first skill it teaches. */
function primaryCategory(baustein: Baustein): SkillCategory | undefined {
    const first = baustein.teaches[0]
    return first ? SKILL_BY_ID.get(first.skillId)?.category : undefined
}

export default function LmsCatalog({
    state,
    onSelect,
}: {
    state: LernpfadDemoState
    onSelect: (bausteinId: string) => void
}) {
    const { localizer } = state
    const { t } = localizer

    const [query, setQuery] = useState("")
    const [format, setFormat] = useState<BausteinFormat | "all">("all")
    const [category, setCategory] = useState<SkillCategory | "all">("all")
    const [provider, setProvider] = useState<string>("all")

    const providers = useMemo(
        () => [...new Set(BAUSTEINE.map((b) => b.provider))].sort((a, b) => a.localeCompare(b)),
        [],
    )

    const results = useMemo(() => {
        const needle = query.trim().toLowerCase()
        return BAUSTEINE.filter((baustein) => {
            if (format !== "all" && baustein.format !== format) return false
            if (category !== "all" && primaryCategory(baustein) !== category) return false
            if (provider !== "all" && baustein.provider !== provider) return false
            if (!needle) return true
            const content = localizer.baustein(baustein)
            return (
                content.title.toLowerCase().includes(needle) ||
                content.description.toLowerCase().includes(needle)
            )
        })
    }, [query, format, category, provider, localizer])

    return (
        <div className="space-y-5 bg-muted/20 p-5">
            <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t("lms.catalog.search")}
                    aria-label={t("lms.catalog.search")}
                    className="h-11 bg-card pl-10 text-sm shadow-sm"
                />
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-2.5 shadow-sm">
                <FacetRow
                    label={t("lms.catalog.filter.format")}
                    value={format}
                    onChange={setFormat}
                    options={FORMATS.map((value) => ({
                        value,
                        label: localizer.format(value),
                        icon: FORMAT_ICON[value],
                    }))}
                    allLabel={t("lms.catalog.filter.all")}
                />
                <FacetRow
                    label={t("lms.catalog.filter.category")}
                    value={category}
                    onChange={setCategory}
                    options={CATEGORIES.map((value) => ({ value, label: localizer.category(value) }))}
                    allLabel={t("lms.catalog.filter.all")}
                />

                <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                        {t("lms.catalog.filter.provider")}
                    </span>
                    <Select value={provider} onValueChange={setProvider}>
                        <SelectTrigger className="h-8 w-[200px] bg-card text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t("lms.catalog.filter.all")}</SelectItem>
                            {providers.map((value) => (
                                <SelectItem key={value} value={value}>
                                    {value}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                {t("lms.catalog.count", { count: results.length })}
            </p>

            {results.length === 0 ? (
                <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t("lms.catalog.empty")}
                </p>
            ) : (
                <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                    {results.map((baustein) => {
                        const content = localizer.baustein(baustein)
                        const Icon = FORMAT_ICON[baustein.format]
                        const category = primaryCategory(baustein)
                        return (
                            <button
                                key={baustein.id}
                                type="button"
                                onClick={() => onSelect(baustein.id)}
                                className="group flex flex-col items-start lp-card rounded-xl border bg-card p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/20 lp-card-hover"
                            >
                                <div className="flex w-full items-start justify-between gap-2">
                                    <span
                                        className={cn(
                                            "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium",
                                            FORMAT_COLOR[baustein.format],
                                        )}
                                    >
                                        <Icon className="size-3" />
                                        {localizer.format(baustein.format)}
                                    </span>
                                    {category && (
                                        <span className="shrink-0 text-xs text-muted-foreground">
                                            {localizer.category(category)}
                                        </span>
                                    )}
                                </div>
                                <span className="mt-3 line-clamp-2 text-sm font-semibold leading-snug text-foreground">
                                    {content.title}
                                </span>
                                <span className="mt-3 flex w-full items-center justify-between border-t pt-2.5 text-xs text-muted-foreground">
                                    <span className="truncate">{content.provider}</span>
                                    <span className="inline-flex shrink-0 items-center gap-1 tabular-nums">
                                        <Clock className="size-3" />
                                        {t("item.hours", { hours: baustein.durationHours })}
                                    </span>
                                </span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

function FacetRow<T extends string>({
    label,
    value,
    onChange,
    options,
    allLabel,
}: {
    label: string
    value: T | "all"
    onChange: (value: T | "all") => void
    options: Array<{ value: T; label: string; icon?: LucideIcon }>
    allLabel: string
}) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">{label}</span>
            <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
                <button
                    type="button"
                    onClick={() => onChange("all")}
                    className={cn(
                        "rounded-md px-2 py-1 text-xs transition-colors",
                        value === "all"
                            ? "bg-background font-medium shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                    )}
                >
                    {allLabel}
                </button>
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => onChange(option.value)}
                        className={cn(
                            "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors",
                            value === option.value
                                ? "bg-background font-medium shadow-sm"
                                : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {option.icon && <option.icon className="size-3" />}
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
